import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import OperatorRegistration from "./pages/OperatorRegistration";
import ExploreExperiences from "./pages/ExploreExperiences";
import TripWizard from "./pages/TripWizard";
import TravelerDashboard from "./pages/TravelerDashboard";
import OperatorDashboard from "./pages/OperatorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TeamDashboard from "./pages/TeamDashboard";
import Destinations from "./pages/Destinations";
import ItineraryWizard from "./pages/ItineraryWizard";
import CuratedItineraryWizard from "./pages/CuratedItineraryWizard";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading, isAuthenticated } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        // Redirect to appropriate dashboard based on role
        if (user?.role === 'admin') return <Navigate to="/admin" replace />;
        if (user?.role === 'operator') return <Navigate to="/operator" replace />;
        if (user?.role === 'team_member') return <Navigate to="/team" replace />;
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    const { user, loading } = useAuth();

    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/operator/register" element={<OperatorRegistration />} />
            <Route path="/experiences" element={<ExploreExperiences />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/trip/create" element={<TripWizard />} />

            {/* Traveler Dashboard */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute allowedRoles={['traveler']}>
                        <TravelerDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Operator Dashboard */}
            <Route 
                path="/operator" 
                element={
                    <ProtectedRoute allowedRoles={['operator']}>
                        <OperatorDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Team Member Dashboard */}
            <Route 
                path="/team" 
                element={
                    <ProtectedRoute allowedRoles={['team_member']}>
                        <TeamDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Itinerary Wizard */}
            <Route 
                path="/operator/create-itinerary/:tripId" 
                element={
                    <ProtectedRoute allowedRoles={['operator', 'team_member']}>
                        <ItineraryWizard />
                    </ProtectedRoute>
                } 
            />

            {/* Curated Itinerary Creation */}
            <Route 
                path="/operator/create-curated" 
                element={
                    <ProtectedRoute allowedRoles={['operator', 'team_member']}>
                        <CuratedItineraryWizard />
                    </ProtectedRoute>
                } 
            />

            {/* Admin Dashboard */}
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboard />
                    </ProtectedRoute>
                } 
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    useEffect(() => {
        const hide = () => {
            const badge = document.getElementById('emergent-badge');
            if (badge) badge.style.cssText = 'display:none!important;visibility:hidden!important;width:0!important;height:0!important;overflow:hidden!important;position:fixed!important;left:-9999px!important;';
        };
        hide();
        const iv = setInterval(hide, 100);
        const obs = new MutationObserver(hide);
        obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
        return () => { clearInterval(iv); obs.disconnect(); };
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster 
                    position="top-center"
                    toastOptions={{
                        style: {
                            background: '#1A1A1A',
                            color: '#fff',
                            borderRadius: '12px',
                        },
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
